import React, { useMemo } from 'react';
import {
    StyleProp,
    TextStyle,
    ViewStyle,
    View,
    ActivityIndicator,
    StyleSheet,
    TouchableOpacity,
    Text,
    ActivityIndicatorProps,
    Platform,
    GestureResponderEvent,
} from 'react-native';
import { isMobile } from '@mightybyte/rnw.utils.device-info';

export interface MB_ButtonStyleProps {
    /**
     * Style of the button container.
     */
    style?: StyleProp<ViewStyle>,

    /**
     * Style of the button text.
     */
    textStyle?: StyleProp<TextStyle>,

    /**
     * Style of the left element wrapper view.
     */
    leftElementStyle?: StyleProp<ViewStyle>,

    /**
     * Style of the right element wrapper view.
     */
    rightElementStyle?: StyleProp<ViewStyle>,
}

const MB_ButtonSetDefaultStyles = (props: MB_ButtonStyleProps) => {
    defaultProps = props;
};

let defaultProps: MB_ButtonStyleProps | undefined;

interface ButtonElementsProps extends Omit<MB_ButtonStyleProps, 'style'> {
    /**
     * Title to be shown inside the button.
     */
    title?: string,

    /**
     * Left element to be shown inside the button.
     */
    leftElement?: JSX.Element,

    /**
     * Right element to be shown inside the button.
     */
    rightElement?: JSX.Element,

    /**
     * If `true`, the button will be disabled. A disabled style will be applied to the button
     * unless `preventDisabledStyleChange` is set to `true`.
     */
    disabled?: boolean,

    /**
     * If `true`, the button will be disabled and will also show a spinner instead of the content.
     * A disabled style will be applied to the button unless `preventDisabledStyleChange` is set to `true`.
     */
    loading?: boolean,
    /**
     * When set to `true`, button style will not be changed when the button is disabled or loading.
     */
    preventDisabledStyleChange?: boolean,

    /**
     * Props for the activity indicator shown when loading is `true`.
     */
    activityIndicatorProps?: ActivityIndicatorProps,
}

interface MB_ButtonProps extends ButtonElementsProps, MB_ButtonStyleProps {
    /**
     * Defines the function to be called when the button is pressed.
     */
    onPress?: (event: GestureResponderEvent) => void,

    /**
     * Defines the function to be called when the button is long pressed.
     */
    onLongPress?: () => void,

    /**
     * Defines the function to be called at the moment the button is pressed.
     */
    onPressIn?: () => void,
    
    /**
     * Defines the function to be called at the moment the button is released.
     */
    onPressOut?: () => void,
    
    /**
     * Delay in ms, from `onPressIn`, before `onLongPress` is called.
     */
    delayLongPress?: number,

    /**
     * Instead of passing text, icons, onPress listeners and so on, you can pass in a component as the child of the button.
     * Note that some of the default elements (icon, right icon and title) of the `MB_Button` will be removed when children are passed.
     *
     * **IMPORTANT:** All the pointer events for children element will be disabled since, otherwise, the button would not work.
     */
    children?: JSX.Element;
}

function shouldUseDisabledStyleGenerator(disabled: boolean, loading: boolean, preventDisabledStyleChange: boolean) {
    return (disabled || loading) && !preventDisabledStyleChange;
}

const ButtonElements = ({ props, elementsOpacity }: { props: ButtonElementsProps, elementsOpacity: number }) => {
    const shouldUseDisabledStyle = shouldUseDisabledStyleGenerator(props.disabled ?? false, props.loading ?? false, props.preventDisabledStyleChange ?? false);

    const leftElementStyle = useMemo(() => {
        let style: ViewStyle = {};
        if (props.leftElement && (props.title || props.rightElement)) {
            style = { marginEnd: 8 };
        }
        return style;
    }, [props.leftElement, props.rightElement, props.title]);

    const rightElementStyle = useMemo(() => {
        let style: ViewStyle = {};
        if (props.rightElement && props.title) {
            style = { marginStart: 8 };
        }
        return style;
    }, [props.rightElement, props.title]);



    return (
        <>
            <View style={[leftElementStyle, props.leftElementStyle, { opacity: elementsOpacity ?? 1 }]} pointerEvents='none'>
                {props.leftElement}
            </View>

            {props.title !== undefined && props.title !== '' &&
                <Text
                    style={[shouldUseDisabledStyle ? styles.titleDisabled : baseStyles.title, props.textStyle, { opacity: elementsOpacity ?? 1 }]}
                >
                    {props.title}
                </Text>
            }
            <View style={[rightElementStyle, props.rightElementStyle, { opacity: elementsOpacity ?? 1 }]} pointerEvents='none'>
                {props.rightElement}
            </View>

            {props.loading &&
                <ActivityIndicator
                    pointerEvents='none'
                    size={props.activityIndicatorProps?.size ?? 'small'}
                    color={props.activityIndicatorProps?.color ?? 'white'}
                    animating={props.activityIndicatorProps?.animating}
                    hidesWhenStopped={props.activityIndicatorProps?.hidesWhenStopped}
                    style={[styles.activityIndicator, props.activityIndicatorProps?.style]}
                />
            }
        </>
    );
};

const MB_ButtonInternal = ({ onPress, onLongPress, onPressIn, onPressOut, delayLongPress, style, children, ...buttonElementsProps }: MB_ButtonProps) => {
    const shouldUseDisabledStyle = shouldUseDisabledStyleGenerator(
        buttonElementsProps.disabled ?? false,
        buttonElementsProps.loading ?? false,
        buttonElementsProps.preventDisabledStyleChange ?? false
    );

    const elementsOpacity = buttonElementsProps.loading ? 0 : shouldUseDisabledStyle ? 0.4 : 1

    return (
        <TouchableOpacity
            style={[
                shouldUseDisabledStyle ? styles.containerStyleDisabled : styles.containerStyle,
                Platform.OS === 'web' && ({ cursor: buttonElementsProps.disabled || buttonElementsProps.loading ? 'not-allowed' : 'pointer' } as ViewStyle),
                style,
            ]}
            // NOTE: we are not using the default disabled prop of touchableOpacity because cursor style is not applied when disabled is true.
            activeOpacity={buttonElementsProps.disabled || buttonElementsProps.loading ? 1 : undefined}
            onPress={buttonElementsProps.disabled || buttonElementsProps.loading ? undefined : onPress}
            onLongPress={buttonElementsProps.disabled || buttonElementsProps.loading ? undefined : onLongPress}
            onPressIn={buttonElementsProps.disabled || buttonElementsProps.loading ? undefined : onPressIn}
            onPressOut={buttonElementsProps.disabled || buttonElementsProps.loading ? undefined : onPressOut}
            delayLongPress={delayLongPress}
        >
            {!children || buttonElementsProps.loading ?
                <ButtonElements props={buttonElementsProps} elementsOpacity={elementsOpacity} />
                :
                <View style={styles.childrenContainer} pointerEvents="none">
                    {children}
                </View>
            }
        </TouchableOpacity>
    );
};

const MB_Button = (props: MB_ButtonProps) => {
    if (defaultProps) {
        const newProps: MB_ButtonStyleProps = {};
        Object.keys(defaultProps).forEach(key => {
            const stylePropsKey = key as keyof MB_ButtonStyleProps;
            newProps[stylePropsKey] = props[stylePropsKey] ? [defaultProps![stylePropsKey], props[stylePropsKey]] : defaultProps![stylePropsKey];
        });

        return <MB_ButtonInternal {...props} {...newProps} />;
    }

    return <MB_ButtonInternal {...props} />;
};

export { MB_Button, MB_ButtonSetDefaultStyles };
export type { MB_ButtonProps };

const baseStyles = StyleSheet.create({
    containerStyle: {
        height: 54,
        borderRadius: 6,
        paddingHorizontal: 16,
        paddingVertical: 8,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
    },
    title: {
        fontSize: isMobile ? 14 : 16,
        fontWeight: '500',
        color: 'white',
        textAlign: 'center',
    },
});

const styles = StyleSheet.create({
    containerStyle: StyleSheet.flatten([
        baseStyles.containerStyle, {
            backgroundColor: '#4369dc',
        },
    ]),
    containerStyleDisabled: StyleSheet.flatten([
        baseStyles.containerStyle, {
            backgroundColor: '#6B687F',
        },
    ]),
    childrenContainer: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    activityIndicator: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
    },
    titleDisabled: StyleSheet.flatten([
        baseStyles.title, {
            color: '#CDD1D9',
        },
    ]),
});
